import { AddCircleOutline, Clear, DeleteOutline, EditOutlined } from '@mui/icons-material'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes, GridRowId } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdCopyAll, MdDelete, MdEdit, MdSend } from 'react-icons/md'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache, useGetCustomerLocalities } from 'src/api/localities'
import { invalidatePositionCache } from 'src/api/positions'
import { useGetAllEligibleServiceStations } from 'src/api/service-station'
import { useGetAllStorageWithCustomers } from 'src/api/storage'
import { useGenerateExcelReports } from 'src/api/system/system-report'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { ReactComponent as ExcelDownload } from 'src/assets/svg/ExcelDownload.svg'
import { hasPermission } from 'src/components/common/Authorization'
import { BulkActionHeaderActions } from 'src/components/customer-admin/equipments/BulkActionHeaderActions'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { OverFlowMenu } from 'src/components/OverFlowMenu'
import { getStatusOptions } from 'src/containers/Equipment/EquipmentFiltersHelper'
import { StyledGenericTable } from 'src/containers/Equipment/EquipmentListContainer'
import { FilterHeader } from 'src/containers/Equipment/FilterHeader'
import FilterChips from 'src/containers/Equipment/filters/FilterChips'
import { CustomerCtx } from 'src/context/CustomerContext'
import { permissionValue } from 'src/enums/permissionValue'
import { MUIThemeColors } from 'src/mui-theme'
import { EquipmentDetailModal } from 'src/screens/localities/mainComponents/EquipmentDetailModal'
import { getThemeColor, getTranslatedText } from 'src/utils'
import {
  getCustomPropertyDisplayValue,
  getCustomPropertyValue,
  parseCustomPropertyValues,
  resolveCustomPropertyLabel,
} from 'src/utils/customPropertyValues'
import { invalidateCertificateCache } from '../../../api/Certificates'
import {
  invalidateMainComponentCache,
  useAddMainComponentToLocality,
  useDiscardEquipmentById,
  useGetExportToExcel,
  useGetMainComponentsByComponentType,
  useUpdateMainComponentToOutgoing,
} from '../../../api/main-component'
import {
  invalidateMainComponentTypeCache,
  useDeleteMainComponentType,
  useGetMainComponentCustomProperties,
} from '../../../api/main-component-type'
import { ColumnOptionButtonType } from '../../../components/table/GenericTable'
import { CANNOT_DISCARD, COMPONENT_EXISTS, NO_VALID_CERTIFICATE } from '../../../constant'
import {
  DialogContextType,
  FacilityTypes,
  MainComponentStatus,
  MonthShortName,
  SystemReportTypes,
} from '../../../enums'
import { IMainComponent, MainComponentFilters } from '../../../generated-types/main-component'
import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import { downloadFileLocal } from '../../../utils/downloadFile'
import { getCageName, getMainComponentStatus, validatePositionAndInstallation } from '../../../utils/function'
import { MainComponentSendModalComponent } from '../../localities/positions/components/MainComponentSendModal'
import { SendToLocalityDialog } from '../../serviceStations/SendToLocalityDialog'
import { UpdateMainComponentTypeDialog } from '../main-component-types/CustomerUpdateMainComponentTypeDialog'
import { CreateMainComponentDialog } from './CreateMainComponentDialog'
import { UpdateMainComponentDialog } from './UpdateMainComponentDialog'
import { GridActionButton } from 'src/components/common/GridActionButton'

interface MainComponentDetailTableProps {
  customerId: EntityId
  componentTypeId: number
  onClickMethod: () => void
  mainComponentTypeName?: string
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  itemCount: number
  searchText?: string
  assignedCustomerId?: number
  searchField?: React.ReactNode
}

export const MainComponentDetailTable: React.FC<MainComponentDetailTableProps> = ({
  customerId,
  componentTypeId,
  onClickMethod,
  mainComponentTypeName,
  localityId,
  mooringId,
  positionId,
  itemCount,
  searchText,
  assignedCustomerId,
  searchField,
}) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000))
  const { t: translate } = useTranslation(['main-component', 'common', 'main-component-type'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { permission } = useCustomerCtx()
  const { mutateAsync, isLoading: isDeleting, error: discardError } = useDiscardEquipmentById(customerId as EntityId)
  const { mutateAsync: deleteMainComponentType, error: errordeleteMainComponentType } = useDeleteMainComponentType(
    customerId as EntityId,
  )
  const exportMutation = useGetExportToExcel(customerId as EntityId)
  const [mainComponentId, setMainComponentId] = useState<EntityId | undefined>()
  const { mutateAsync: updateMainComponentToOutgoing } = useUpdateMainComponentToOutgoing(
    customerId,
    mainComponentId as EntityId,
  )
  const [selectedPositionId, setSelectedPositionId] = useState<EntityId>(0)
  const { mutateAsync: addMainComponentToLocality } = useAddMainComponentToLocality(customerId, selectedPositionId)
  const { mutateAsync: generateExcel, isLoading: isGeneratingExcel } = useGenerateExcelReports()
  const { data: customProperties } = useGetMainComponentCustomProperties(componentTypeId, customerId as EntityId)
  const { data: localities, isLoading: localitiesLoading } = useGetCustomerLocalities({
    clientId: customerId as EntityId,
  })
  const { data: serviceStations, isLoading: serviceStationsLoading } = useGetAllEligibleServiceStations(customerId)
  const { data: storages, isLoading: storagesLoading } = useGetAllStorageWithCustomers(customerId)

  const isLocationDataLoading = localitiesLoading || serviceStationsLoading || storagesLoading

  const { data: equipment = [], isLoading } = useGetMainComponentsByComponentType(
    customerId as EntityId,
    componentTypeId,
    searchText,
  )

  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])
  const [localFilters, setLocalFilters] = useState<MainComponentFilters>({
    serialNumber: '',
    description: '',
    supplier: '',
    orderNumber: '',
    dateProduced: '',
    status: [],
    where: [],
    hs: '',
    vc5: '',
    vc15: '',
    circumference: '',
    solidity: '',
  })

  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const getLocationOptions = useMemo(() => {
    const options: Array<{ value: string | number; label: string }> = []

    if (localities) {
      localities?.forEach(locality => {
        options.push({
          value: `${locality.id}#${FacilityTypes.locality}`,
          label: locality.name,
        })
      })
    }

    if (serviceStations) {
      serviceStations.forEach(serviceStation => {
        options.push({
          value: `${serviceStation.id}#${FacilityTypes.serviceStation}`,
          label: serviceStation.name,
        })
      })
    }

    if (storages) {
      storages.forEach(storage => {
        options.push({
          value: `${storage.id}#${FacilityTypes.storage}`,
          label: storage.name,
        })
      })
    }

    return options
  }, [localities, serviceStations, storages])

  const filteredEquipment = useMemo(() => {
    if (!equipment || equipment.length === 0) return []

    return equipment.items.filter((item: IMainComponent) => {
      const getStringValue = (value: any): string => {
        if (value === null || value === undefined) return ''
        return String(value).toLowerCase()
      }

      for (const [field, filterValue] of Object.entries(localFilters)) {
        if (!filterValue || (typeof filterValue === 'string' && !filterValue.trim())) continue

        const filterLower = Array.isArray(filterValue)
          ? filterValue.map(v => (typeof v === 'string' ? v.toLowerCase() : v))
          : typeof filterValue === 'string'
          ? filterValue.toLowerCase()
          : filterValue

        switch (field) {
          case 'serialNumber':
            if (!getStringValue(item.serialNumber).includes(filterLower)) return false
            break
          case 'description':
            if (!getStringValue(item.description).includes(filterLower)) return false
            break
          case 'supplier':
            if (!getStringValue(item.supplier?.name).includes(filterLower)) return false
            break
          case 'orderNumber':
            if (!getStringValue(item.orderNumber).includes(filterLower)) return false
            break
          case 'dateProduced': {
            const producedYear = item.productionYear ? String(item.productionYear) : ''
            const producedMonth = item.productionMonth ? String(item.productionMonth + 1).padStart(2, '0') : ''
            const dateProduced = `${producedYear}${producedMonth ? '-' + producedMonth : ''}`.toLowerCase()

            if (!dateProduced.startsWith(filterLower)) return false
            break
          }
          case 'status': {
            if (Array.isArray(filterLower) && filterLower.length > 0) {
              if (!filterLower.some(status => status === item.status)) {
                return false
              }
            }
            break
          }
          case 'where': {
            if (Array.isArray(filterLower) && filterLower.length > 0) {
              let itemMatchesAnyLocation = false

              for (const location of filterLower) {
                const [locationId, locationType] = location.split('#')

                if (locationType === String(FacilityTypes.locality)) {
                  if (
                    item.selectedLocalityId === parseInt(locationId) &&
                    (item.status === MainComponentStatus.Active ||
                      item.status === MainComponentStatus.Outgoing ||
                      item.status === MainComponentStatus.InLocalityStock)
                  ) {
                    itemMatchesAnyLocation = true
                    break
                  }
                } else if (locationType === String(FacilityTypes.serviceStation)) {
                  if (
                    item.componentServiceStations?.some(
                      cs => cs.serviceStationId === parseInt(locationId) && cs.isActive,
                    )
                  ) {
                    itemMatchesAnyLocation = true
                    break
                  }
                } else if (locationType === String(FacilityTypes.storage)) {
                  if (item.storedEquipment?.some(se => se.storageId === parseInt(locationId))) {
                    itemMatchesAnyLocation = true
                    break
                  }
                }
              }

              if (!itemMatchesAnyLocation) {
                return false
              }
            }
            break
          }
          case 'circumference':
            if (!getStringValue(item.circumference).includes(filterLower)) return false
            break
          case 'solidity':
            if (!getStringValue(item.solidity).includes(filterLower)) return false
            break
          case 'hs': {
            const hsValues = item.mainComponentProperties?.map(prop => getStringValue(prop.hs))
            if (!hsValues || !hsValues.includes(filterLower)) return false
            break
          }
          case 'vc5': {
            const vc5Values = item.mainComponentProperties?.map(prop => getStringValue(prop.vC5))
            if (!vc5Values || !vc5Values.includes(filterLower)) return false
            break
          }
          case 'vc15': {
            const vc15Values = item.mainComponentProperties?.map(prop => getStringValue(prop.vC15))
            if (!vc15Values || !vc15Values.includes(filterLower)) return false
            break
          }
          default: {
            if (field.startsWith('customProperty_')) {
              const customPropertiesData = parseCustomPropertyValues(item.customPropertyValues)
              const propertyId = field.replace('customProperty_', '')
              const property = customProperties?.find(p => p.id.toString() === propertyId)
              if (property) {
                const value = getStringValue(getCustomPropertyValue(customPropertiesData, property))
                if (!value.includes(filterLower)) return false
              }
            }
            break
          }
        }
      }

      return true
    })
  }, [equipment, localFilters, customProperties, translate])

  const selectedRows = useMemo(
    () => filteredEquipment.filter(row => selectionModel.includes(row.id) && !row.isDeleted),
    [filteredEquipment, selectionModel],
  )

  const updateFilter = useCallback((field: string, value: string | number | string[] | number[]) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: Array.isArray(value)
        ? value.map(v => (typeof v === 'string' ? v.trim() : v))
        : typeof value === 'string'
        ? value.trim()
        : value,
    }))
  }, [])

  // Clear specific filter
  const clearFilter = useCallback((field: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: Array.isArray(prev[field]) ? [] : '',
    }))

    if (filterInputRefs.current[field]) {
      filterInputRefs.current[field]!.value = ''
    }
  }, [])

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const clearedFilters: MainComponentFilters = {
      serialNumber: '',
      description: '',
      supplier: '',
      orderNumber: '',
      dateProduced: '',
      status: [],
      where: [],
      circumference: '',
      solidity: '',
    }

    if (customProperties) {
      customProperties.forEach(property => {
        clearedFilters[`customProperty_${property.id}`] = ''
      })
    }

    setLocalFilters(clearedFilters)

    Object.keys(filterInputRefs.current).forEach(field => {
      if (filterInputRefs.current[field]) {
        filterInputRefs.current[field]!.value = ''
      }
    })
  }, [customProperties])

  const createFilterHeader = useCallback(
    (
      headerName: string,
      field: string,
      filterType: 'text' | 'number' | 'date' | 'dropdown' = 'text',
      options?: {
        placeholder?: string
        dropdownOptions?: Array<{ value: string | number; label: string }>
        loading?: boolean
        multiple?: boolean
      },
    ) => {
      return (
        <FilterHeader
          headerName={headerName}
          field={field}
          filterType={filterType}
          placeholder={options?.placeholder}
          dropdownOptions={options?.dropdownOptions}
          loading={options?.loading}
          multiple={options?.multiple}
          filters={localFilters}
          filterInputRefs={filterInputRefs}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
        />
      )
    },
    [localFilters, updateFilter, clearFilter],
  )

  const onUpdate = useCallback(
    async (id, searchText) => {
      const added = await showDialog(
        UpdateMainComponentDialog,
        {
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: {
            id: id as EntityId,
            mainComponentTypeName: mainComponentTypeName,
            customerId: customerId as EntityId,
            searchText: searchText,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [mainComponentTypeName],
  )
  const onClone = useCallback(
    async (row, searchText) => {
      const { serialNumber, name, id, certificates, ...rest } = row
      const added = await showDialog(
        CreateMainComponentDialog,
        {
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: {
            customerId: customerId as EntityId,
            initialValues: {
              componentTypeId: componentTypeId,
              ...rest,
              positionId: null,
              certificateNumber: null,
              certificateSupplierId: null,
              certificateDocumentType: 1,
              certificateDate: null,
              certificateFileId: null,
            },
            mainComponentTypeName: mainComponentTypeName,
            searchText: searchText,
            clonedFromEquipment: row as IMainComponent,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [customerId, mainComponentTypeName],
  )

  useEffect(() => {
    if (discardError && discardError['data'] == CANNOT_DISCARD) {
      toast(translate('cannotDiscardActiveMainComponent'), { type: 'error' })
    }
    if (errordeleteMainComponentType && errordeleteMainComponentType['data'] == COMPONENT_EXISTS) {
      toast(translate('component-exists', { ns: 'main-component-type' }), { type: 'error' })
    }
  }, [discardError, errordeleteMainComponentType])

  const onDeleteMainComponent = (id: EntityId, searchText) => {
    showConfirmDialog(
      t('discardMainComponent', { ns: 'main-component' }),
      t('areYouSureDiscardMainComponent', { ns: 'main-component' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await mutateAsync(
          { id, shouldNotifyClientAdmins: false },
          {
            onSuccess: () => {
              toast.success(t('mainComponentDiscarded', { ns: 'main-component' }))
              invalidateMainComponentCache.getMainComponentsByComponentType(customerId, componentTypeId, searchText)
              invalidateMainComponentCache.getDiscardedMainComponents(customerId)
              invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(customerId)
              invalidateMainComponentCache.invalidateEquipment(customerId, { localityId })
              invalidateMainComponentCache.getMaincomponentsBySearch(customerId)
              invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
              pop()
            },
            onError: () => {
              pop()
            },
          },
        )
      },
    )
  }

  const onSend = async (id: EntityId, searchText) => {
    const updated = await showDialog(MainComponentSendModalComponent, {
      title: translate('sendTo', { ns: 'main-component' }),
      componentProps: {
        customerId,
        localityId,
        mooringId,
        positionId,
        id,
        onlyServiceStation: false,
        searchText: searchText,
      },
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    })
    if (!updated) return
  }

  const onSubmit = useCallback(
    async (form, pop, formContext) => {
      if (!validatePositionAndInstallation(form, translate, formContext)) return
      if (form.selectedPositionId != null) {
        setSelectedPositionId(form.selectedPositionId)
        await addMainComponentToLocality(form, {
          onSuccess: () => {
            invalidateMainComponentCache.getMainComponents(customerId, localityId, mooringId, selectedPositionId)
            invalidateMainComponentCache.getOutgoingMainComponents(customerId, localityId)
            invalidateCertificateCache.getCertificatesByPosition(customerId, selectedPositionId)
            invalidatePositionCache.getCagePositionDeviations(customerId, localityId, selectedPositionId)
            invalidateMainComponentCache.getMainComponentsByComponentType(
              customerId,
              form.mainComponentTypeId as EntityId,
              searchText,
            )
            invalidateMainComponentCache.getMainComponentById(customerId, form.mainComponentId ?? 0)
            invalidateMainComponentCache.getMaincomponentsBySearch(customerId, searchText)
            invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
            invalidateDocument.getDocuments(customerId)
            invalidateDocument.getDocumentsForApproval(customerId, localityId)
            invalidateLocalityCache.getCageStatus(customerId, localityId)
            invalidateLocalityCache.getLocality(customerId, localityId as EntityId)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
            invalidateDeviation.getDeviations(customerId)
            toast.success(translate('successfully-sent-to-locality', { ns: 'main-component' }))
            pop()
          },
          onError: error => {
            if (error && error['data'] == 'ExceedMaxCountForCage') {
              toast(t('exceedMaxCountForCage', { ns: 'main-component' }), { type: 'error' })
            }
            if (error && error['data'] == NO_VALID_CERTIFICATE) {
              toast(t('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
            }
            if (error['data'] == 'CertificateNumberExists') {
              toast(translate('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
              if (formContext && typeof formContext.setError === 'function') {
                formContext.setError('certificateNumber', {
                  type: 'manual',
                  message: translate('certificateNumberExists', { ns: 'main-component' }),
                })
              }
            }
          },
        })
      } else {
        await updateMainComponentToOutgoing(form, {
          onSuccess: () => {
            invalidateMainComponentCache.getMainComponentsByComponentType(
              customerId,
              form.mainComponentTypeId as EntityId,
              searchText,
            )
            invalidateMainComponentCache.getMaincomponentsBySearch(customerId, searchText)
            invalidateMainComponentCache.getMainComponentById(customerId, form.mainComponentId ?? 0)
            invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
            invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
            pop()
            toast.success(translate('successfully-sent-to-locality', { ns: 'main-component' }))
          },
          onError: error => {
            if (error && error['data'] == NO_VALID_CERTIFICATE) {
              pop()
              toast(t('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
            }
            if (error['data'] == 'CertificateNumberExists') {
              toast(translate('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
              if (formContext && typeof formContext.setError === 'function') {
                formContext.setError('certificateNumber', {
                  type: 'manual',
                  message: translate('certificateNumberExists', { ns: 'main-component' }),
                })
              }
            }
          },
        })
      }
    },
    [customerId, localityId, mooringId, selectedPositionId, mainComponentId],
  )

  const onSendToOutgoing = useCallback(
    async (mainComponent: IMainComponent) => {
      setMainComponentId(mainComponent.id)
      const updated = await showDialog(
        SendToLocalityDialog,
        {
          componentProps: {
            customerId,
            mainComponentId: mainComponent.id as EntityId,
            mainComponentTypeId: mainComponent.typeId as EntityId,
            onSubmit,
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
      if (!updated) return
    },
    [customerId, mainComponentId],
  )

  const renderMainComponentLocation = useCallback(params => {
    const mainComponent = params.row as IMainComponent

    if (mainComponent) {
      if (mainComponent.selectedPositionName) {
        return (
          <Box>
            {mainComponent.selectedLocalityName && <h5>{mainComponent.selectedLocalityName + '-'} </h5>}
            <h6>{getCageName(mainComponent.selectedPositionName)}</h6>
          </Box>
        )
      } else if (mainComponent.selectedLocalityName) {
        return (
          <Box>
            <h5>{mainComponent.selectedLocalityName ?? '-'} </h5>
          </Box>
        )
      } else if (mainComponent.assignedServiceStationName) {
        return (
          <Box>
            <h5>{mainComponent.assignedServiceStationName ?? '-'} </h5>
          </Box>
        )
      } else {
        return (
          <Box>
            <h5>{mainComponent.storageName ?? '-'} </h5>
          </Box>
        )
      }
    }

    return <h5>{'-'}</h5>
  }, [])

  const onUpdateMainComponentType = useCallback(
    async id => {
      const added = await showDialog(
        UpdateMainComponentTypeDialog,
        {
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: { id: id as EntityId, customerId: customerId as EntityId },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [customerId],
  )

  const onDeleteMainComponentType = useCallback(
    (id: EntityId) => {
      showConfirmDialog(
        translate('deleteMainComponentType', { ns: 'main-component-type' }),
        translate('areYouSureDeleteMainComponentType', { ns: 'main-component-type' }),
        undefined,
        async pop => {
          await deleteMainComponentType(id, {
            onSuccess: () => {
              toast(translate('type-deleted', { ns: 'main-component-type' }), { type: 'success' })
              invalidateMainComponentTypeCache.useGetMainComponentTypes(customerId as EntityId)
              invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(customerId)
              pop()
            },
            onError: () => {
              toast.error(translate('deletingError', { ns: 'common' }))
            },
          })
        },
      )
    },
    [customerId],
  )

  const customPropertyColumns: GridColumns = useMemo(() => {
    if (!customProperties || customProperties.length === 0) return []

    return customProperties.map(property => ({
      field: `customProperty_${property.id.toString()}`,
      headerName: resolveCustomPropertyLabel(property),
      flex: 0.75,
      minWidth: 100,
      renderHeader: () =>
        createFilterHeader(resolveCustomPropertyLabel(property), `customProperty_${property.id.toString()}`),
      renderCell: params => (
        <Typography fontWeight={400}>
          {getCustomPropertyDisplayValue(params.row.customPropertyValues, property)}
        </Typography>
      ),
    }))
  }, [customProperties, createFilterHeader])

  const componentProperties: GridColumns = [
    {
      field: 'hs',
      headerName: translate('hs', { ns: 'main-component-property' }),
      flex: 1,
      renderHeader: () => createFilterHeader(translate('hs', { ns: 'main-component-property' }), 'hs'),
      renderCell: params => {
        const componentProperties = params.row.mainComponentProperties
        const hsFilter = localFilters.hs?.toString()
        const hsValues = componentProperties
          .filter(prop => !hsFilter || prop.hs?.toString() === hsFilter)
          .map((set, index) => ({
            id: index,
            name: set.hs,
          }))
        return <OverFlowMenu key="hs" visibleCount={1} items={hsValues} enableMoreOption />
      },
    },
    {
      field: 'vc5',
      headerName: translate('vc5', { ns: 'main-component-property' }),
      flex: 1,
      renderHeader: () => createFilterHeader(translate('vc5', { ns: 'main-component-property' }), 'vc5'),
      renderCell: params => {
        const componentProperties = params.row.mainComponentProperties
        const vc5Filter = localFilters.vc5?.toString()
        const vc5Values = componentProperties
          .filter(prop => !vc5Filter || prop.vC5?.toString() === vc5Filter)
          .map((set, index) => ({
            id: index,
            name: set.vC5,
          }))
        return <OverFlowMenu key="vc5" visibleCount={1} items={vc5Values} enableMoreOption />
      },
    },
    {
      field: 'vc15',
      headerName: translate('vc15', { ns: 'main-component-property' }),
      flex: 1,
      renderHeader: () => createFilterHeader(translate('vc15', { ns: 'main-component-property' }), 'vc15'),
      renderCell: params => {
        const componentProperties = params.row.mainComponentProperties
        const vc15Filter = localFilters.vc15?.toString()
        const vc15Values = componentProperties
          .filter(prop => prop.vC15 !== null && (!vc15Filter || prop.vC15?.toString() === vc15Filter))
          .map((set, index) => ({
            id: index,
            name: set.vC15,
          }))
        return <OverFlowMenu key="vc15" visibleCount={1} items={vc15Values} enableMoreOption />
      },
    },
  ]

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'serialNumber',
      headerName: translate('serialNumber', { ns: 'main-component' }),
      flex: 1,
      minWidth: 100,
      renderHeader: () => createFilterHeader(translate('serialNumber', { ns: 'main-component' }), 'serialNumber'),
    },
    {
      field: 'description',
      headerName: translate('description', { ns: 'common' }),
      flex: 2,
      minWidth: 120,
      renderHeader: () => createFilterHeader(translate('description', { ns: 'common' }), 'description'),
      renderCell: ({ row }) => <DynamicTextTypography text={row.description} maxLength={40} fontWeight={500} />,
    },
    {
      field: 'supplier',
      headerName: translate('Supplier', { ns: 'main-component' }),
      flex: 1,
      minWidth: 130,
      renderHeader: () => createFilterHeader(translate('Supplier', { ns: 'main-component' }), 'supplier'),
      valueGetter: ({ row }) => {
        return row?.supplier?.name ?? '-'
      },
    },
    {
      field: 'orderNumber',
      headerName: translate('orderNumber', { ns: 'main-component' }),
      flex: 1,
      minWidth: 120,
      renderHeader: () => createFilterHeader(translate('orderNumber', { ns: 'main-component' }), 'orderNumber'),
    },
    {
      field: 'dateProduced',
      headerName: translate('dateProduced', { ns: 'main-component' }),
      flex: 0.8,
      minWidth: 120,
      renderHeader: () =>
        createFilterHeader(translate('dateProduced', { ns: 'main-component' }), 'dateProduced', 'date'),
      renderCell: ({ row }) => {
        const producedYear = row?.productionYear != null ? row?.productionYear : '-'
        const producedMonth =
          row?.productionYear != null && row?.productionMonth != null ? '-' + MonthShortName[row?.productionMonth] : ''
        const dateProduced = producedYear + producedMonth
        return <DynamicTextTypography text={dateProduced} variant="body1" maxLength={10} fontWeight={400} />
      },
    },
    {
      field: 'status',
      headerName: translate('status', { ns: 'work-assignment-position' }),
      flex: 1,
      minWidth: 130,
      renderHeader: () =>
        createFilterHeader(translate('status', { ns: 'work-assignment-position' }), 'status', 'dropdown', {
          dropdownOptions: getStatusOptions(translate),
          multiple: true,
        }),
      renderCell: params => <h5>{translate(getMainComponentStatus(params.row), { ns: 'main-component' })}</h5>,
    },
    {
      field: 'where',
      headerName: translate('where', { ns: 'main-component' }),
      flex: 1.2,
      minWidth: 180,
      renderHeader: () =>
        createFilterHeader(translate('where', { ns: 'main-component' }), 'where', 'dropdown', {
          dropdownOptions: getLocationOptions,
          loading: isLocationDataLoading,
          multiple: true,
        }),
      renderCell: renderMainComponentLocation,
    },
    {
      field: 'circumference',
      headerName: translate('circumference', { ns: 'main-component' }),
      flex: 0.75,
      minWidth: 80,
      renderHeader: () => createFilterHeader(translate('circumference', { ns: 'main-component' }), 'circumference'),
      renderCell: params => {
        const circumference = params.row.circumference
        return <Typography fontWeight={400}>{circumference ? `${circumference}` : '-'}</Typography>
      },
    },
    {
      field: 'solidity',
      headerName: translate('solidity', { ns: 'main-component' }),
      flex: 0.75,
      minWidth: 80,
      renderHeader: () => createFilterHeader(translate('solidity', { ns: 'main-component' }), 'solidity'),
      renderCell: params => {
        const solidity = params.row.solidity
        return <Typography fontWeight={400}>{solidity ? `${solidity}` : '-'}</Typography>
      },
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      ...customPropertyColumns,
      ...componentProperties,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: translate('actions', { ns: 'common' }),
        width: 60,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={id}
            label={t('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(id, searchText as string)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={t('clone', { ns: 'main-component' })}
            icon={<MdCopyAll size={24} />}
            onClick={() => onClone(row, searchText)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={t('discard', { ns: 'common' })}
            icon={<MdDelete size={24} style={{ color: getThemeColor(theme, MUIThemeColors.errorMain) }} />}
            onClick={() => onDeleteMainComponent(id, searchText)}
            disabled={
              row.status === MainComponentStatus.Active ||
              row.status === MainComponentStatus.Outgoing ||
              row.status === MainComponentStatus.InLocalityStock ||
              isDeleting ||
              !hasPermission(
                [permissionValue.ClientAdminPermission, permissionValue.ClientAdminPortal.Equipment_Portal],
                permission,
              )
            }
            sx={{
              color: getThemeColor(theme, MUIThemeColors.errorMain),
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('sendTo', { ns: 'main-component' })}
            icon={<MdSend size={24} />}
            onClick={() => onSend(id, searchText)}
            disabled={row.status != MainComponentStatus.NewlyRegistered}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('sendToOutgoing', { ns: 'main-component' })}
            icon={<MdSend size={24} />}
            onClick={() => {
              onSendToOutgoing(row as IMainComponent)
            }}
            disabled={row.status != MainComponentStatus.NewlyRegistered}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [searchText, customPropertyColumns, createFilterHeader],
  )

  const handleExcelDownload = async () => {
    const filters: Record<string, any> = {}
    const customPropertyFilters: string[] = []

    Object.entries(localFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const trimmedValues = value.filter(v => typeof v === 'string' && v.trim())
        if (trimmedValues.length > 0) {
          filters[key] = trimmedValues
        } else {
          const values = value.filter(v => typeof v === 'number')
          filters[key] = values.length > 0 ? values : []
        }
      } else if (typeof value === 'string' && value.trim()) {
        if (key.startsWith('customProperty_')) {
          const propertyId = key.replace('customProperty_', '')
          const property = customProperties?.find(p => p.id.toString() === propertyId)

          if (property) {
            const propertyId = property.id.toString()
            customPropertyFilters.push(`${propertyId}#${value.trim()}`)
          }
        } else {
          filters[key] = value.trim()
        }
      }
    })

    if (customPropertyFilters.length > 0) {
      filters.customProperties = customPropertyFilters
    }

    await generateExcel(
      {
        reportId: SystemReportTypes.EquipmentTypeReport,
        customerId: customerId,
        typeId: componentTypeId,
        filters: filters,
      },
      {
        onSuccess: data => {
          downloadFileLocal(
            data,
            translate('main-components', { ns: 'header' }) + '-' + new Date(Date.now()).toISOString(),
          )
        },
      },
    )
  }

  const enhancedToolbarOptions = useMemo(() => {
    const hasActiveFilters = Object.values(localFilters).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0
      }
      if (typeof value === 'string') {
        return value.trim().length > 0
      }
      return value !== null && value !== undefined
    })

    const baseOptions = [
      ...(searchField
        ? [
            {
              label: '',
              Component: searchField,
            },
          ]
        : []),
      {
        label: '',
        Component: (
          <Box overflow={'hidden'} marginBottom={'-5px !important'}>
            <BulkActionHeaderActions
              customerId={customerId as EntityId}
              data={selectedRows}
              locality={{ id: localityId }}
            />
          </Box>
        ),
      },
      {
        label: translate('addMainComponent'),
        Component: (
          <GridActionButton
            buttonText={isSmallScreen ? '' : translate('addMainComponent', { ns: 'main-component' })}
            buttonIcon={<AddCircleOutline />}
            onClick={onClickMethod}
            height="36px"
            width={isSmallScreen ? '36px' : 'auto'}
          />
        ),
      },
      {
        label: translate('updateMainComponentType', { ns: 'main-component-type' }),
        Component: (
          <GridActionButton
            buttonIcon={<EditOutlined />}
            onClick={() => onUpdateMainComponentType(componentTypeId)}
            height="36px"
            width="36px"
          />
        ),
      },
      {
        label: translate('deleteMainComponentType', { ns: 'main-component-type' }),
        Component: (
          <GridActionButton
            buttonIcon={<DeleteOutline />}
            onClick={() => {
              onDeleteMainComponentType(componentTypeId)
            }}
            height="36px"
            width="36px"
            disabled={assignedCustomerId === null ? true : false}
          />
        ),
      },
      {
        label: translate('system-report-excel-download', { ns: 'document' }),
        Component: (
          <GridActionButton
            buttonIcon={
              <ExcelDownload height={'20px'} width={'15px'} color={getThemeColor(theme, MUIThemeColors.primaryMain)} />
            }
            loading={isGeneratingExcel}
            height="36px"
            width="36px"
            onClick={handleExcelDownload}
            variant="outlined"
            sx={{ marginTop: '10px' }}
          />
        ),
      },
    ]

    if (hasActiveFilters) {
      baseOptions.push({
        label: translate('clearFilters', { ns: 'common' }),
        Component: (
          <GridActionButton
            buttonText={translate('clearFilters', { ns: 'common' })}
            buttonIcon={<Clear />}
            onClick={clearAllFilters}
            height="36px"
            width="auto"
          />
        ),
      })
    }

    return baseOptions
  }, [
    localFilters,
    clearAllFilters,
    isSmallScreen,
    onClickMethod,
    componentTypeId,
    handleExcelDownload,
    isGeneratingExcel,
    customerId,
    localityId,
    selectedRows,
    searchField,
  ])

  const handleOnClick = (mainComponent: IMainComponent) => {
    showDialog(
      EquipmentDetailModal,
      {
        componentProps: {
          customerId: customerId as EntityId,
          permission: permission,
          mainComponentId: mainComponent.id,
          mainComponentTypeId: mainComponent.type.id,
          isWithLayout: true,
          context: CustomerCtx,
        },
        opts: {
          maxWidth: false,
          fullWidth: true,
          bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
          rootWidth: '1400px',
        },
      },
      DialogContextType.customerAdminContext,
      true,
    )
  }

  return (
    <>
      <StyledGenericTable
        id={'main-components' + componentTypeId}
        data={filteredEquipment}
        loading={isLoading}
        columns={[...columns]}
        checkboxSelection
        disableSelectionOnClick
        selectionModel={selectionModel}
        onSelectionModelChange={newSelection => setSelectionModel(newSelection)}
        defaultColumnOption={[
          ...STATIC_COLUMNS.map(c => ({ field: c.field, headerName: c.headerName as string })),
          { field: 'actions', headerName: translate('actions', { ns: 'common' }) },
        ]}
        actionColumns={[]}
        enableToolbar={true}
        enableToolbarFilter={true}
        toolbarFilter={
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} color={theme.palette.primary.main}>
              {`${getTranslatedText(mainComponentTypeName as string)} ${translate('equipments', {
                ns: 'main-component',
              })}`}
              {Object.values(localFilters).some(value => {
                if (Array.isArray(value)) {
                  return value.length > 0
                }
                if (typeof value === 'string') {
                  return value.trim().length > 0
                }
                if (typeof value === 'number') {
                  return true
                }
                return value !== null && value !== undefined
              }) && <Typography component="span"> ({translate('filtered', { ns: 'common' })})</Typography>}
            </Typography>
            <FilterChips
              headerFilters={localFilters}
              clearFilter={clearFilter}
              updateFilter={updateFilter}
              localities={localities ?? []}
              serviceStations={serviceStations}
              storages={storages}
            />
          </Box>
        }
        toolbarOptions={enhancedToolbarOptions}
        showHeaderOnEmptyState={true}
        enableColumnOptions={true}
        enableExport={itemCount > 0}
        columnOptionButttonType={ColumnOptionButtonType.GridAction}
        isDownloading={exportMutation.isLoading}
        onDownload={async () => {
          exportMutation
            .mutateAsync({ customerId: customerId as EntityId, typeId: componentTypeId as number })
            .then(res => {
              downloadFileLocal(res, `${JSON.parse(mainComponentTypeName ?? '{en:download}').en}`)
            })
        }}
        onRowClick={({ row }) => handleOnClick(row as IMainComponent)}
        autoHeight={false}
        height={window.innerHeight - 240 + 'px'}
        sx={{
          '& .MuiDataGrid-row:last-child': {
            marginBottom: '16px',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.75rem',
            fontWeight: 400,
          },
          '& .MuiDataGrid-cell h5, & .MuiDataGrid-cell h6, & .MuiDataGrid-cell .MuiTypography-root': {
            fontSize: '0.75rem',
            fontWeight: 400,
            margin: 0,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.75rem',
          },
        }}
        noDataComponent={() => {
          return <EmptyStatusCard imageWidth="500px" />
        }}
        toolBarStyles={{ marginBottom: '8px !important' }}
        allowHorizontalScroll={true}
      />
    </>
  )
}
